/**
 * Branding helper utilities
 * Provides easy access to theme-specific branding information
 */

import { useSettingsStore } from '../store/tenantStore';
import { getPresetById, type ColorPreset } from '../config/themePresets';

/**
 * Hook to access current theme's branding information
 * @returns Branding data based on active theme
 */
export function useBranding() {
  const settings = useSettingsStore((state) => state.settings);
  const theme = getPresetById(settings?.colorTheme ?? '301io');
  
  return {
    /** Company name (e.g., "301io", "BrandsMart USA") */
    brandName: theme.brandName ?? theme.name,
    /** Product name to display in UI (e.g., "HomeIQ", "BrandsMart") */
    productName: theme.productName ?? theme.name,
    /** Logo file path */
    logoPath: theme.logoPath ?? '',
    /** Brand tagline / slogan */
    tagline: theme.tagline ?? '',
    /** Deal badge text for promotions (e.g., "HOMEIQ DEAL") */
    dealBadge: theme.dealBadgeName ?? 'SPECIAL DEAL',
    /** Deal provider name (e.g., "HomeIQ", "BrandsMart") */
    dealProvider: theme.dealProviderName ?? theme.productName ?? theme.name,
    /** Full theme preset for advanced usage */
    theme,
  };
}

/**
 * Get branding data for a specific theme (non-hook version)
 * @param themeId - Theme ID ('301io' or 'brandsmart')
 * @returns Branding data for the specified theme
 */
export function getBrandingForTheme(themeId: string) {
  const theme = getPresetById(themeId);
  
  return {
    brandName: theme.brandName ?? theme.name,
    productName: theme.productName ?? theme.name,
    logoPath: theme.logoPath ?? '',
    tagline: theme.tagline ?? '',
    dealBadge: theme.dealBadgeName ?? 'SPECIAL DEAL',
    dealProvider: theme.dealProviderName ?? theme.productName ?? theme.name,
    theme,
  };
}

/**
 * Get theme-specific image path from src/Images folder
 * @param filename - Image filename
 * @returns Full import path
 */
export function getThemeImage(filename: string) {
  return `/src/Images/${filename}`;
}
